/**
 * Booking-day policy — the single interpreter of `temple.ONLINE_DAY_END`.
 *
 * Grammar (owner decision 2026-07-16, backward compatible):
 *   "8:00 PM"        legacy bare time  -> min day = tomorrow until the cutoff, then +2
 *   "" / null / junk                   -> min day = tomorrow, no cutoff (legacy fallback)
 *   "D0" / "D3" ...  offset form       -> min day = today + N, bookable any time
 *   "D4|8:00 PM"     offset + cutoff   -> today + 4 until 8 PM, then today + 5
 *   "D-7"            past window       -> bookable from 7 days ago onwards
 *   "PAST"           unlimited past    -> no minimum date at all
 *
 * Every screen that used to read `onlineDayEnd` directly (portal glass screen,
 * panchang calendar, checkout revalidation, counter date gating) must derive its
 * minimum through these helpers — never re-implement the math.
 *
 * Timezone (owner directive 2026-07-16): the app is Kerala-only, so "today"
 * and the cutoff are evaluated on the IST (Asia/Kolkata) wall clock of the
 * server-synced instant, never the browser's zone. Returned minimum dates are
 * instants at IST midnight — decompose/format them with the ist helpers.
 */
import { istEpoch, istParts, istStartOfDay } from '@otas/utils/custom-components/panchang-calendar/ist';

export interface CutoffTime {
    hour: number;   // 0-23
    minute: number; // 0-59
}

export interface BookingDayPolicy {
    /** Days from today of the earliest bookable date (0=today, 1=tomorrow,
     *  negative=past window). `null` = unlimited past ("PAST"). */
    minOffset: number | null;
    /** After this time of day the minimum moves one day later. `null` = no cutoff. */
    cutoff: CutoffTime | null;
}

/** The behavior when ONLINE_DAY_END is empty or unparseable (legacy fallback: tomorrow). */
export const DEFAULT_BOOKING_DAY_POLICY: BookingDayPolicy = { minOffset: 1, cutoff: null };

/**
 * Parse a time-of-day in the same shapes legacy code accepted via
 * moment(x, ['h:m a', 'H:m']): "8:00 PM", "8 pm", "08:00", "20:00", "20:5".
 * Returns null when the text is not a valid time.
 */
export function parseCutoffTime(raw: string | null | undefined): CutoffTime | null {
    if (!raw) { return null; }
    const m = /^\s*(\d{1,2})(?:[:.](\d{1,2}))?\s*([AaPp])\.?[Mm]?\.?\s*$/.exec(raw.trim())
        || /^\s*(\d{1,2})(?:[:.](\d{1,2}))\s*$/.exec(raw.trim());
    if (!m) { return null; }
    let hour = +m[1];
    const minute = +(m[2] || 0);
    const meridiem = (m[3] || '').toLowerCase();
    if (minute > 59) { return null; }
    if (meridiem) {
        if (hour < 1 || hour > 12) { return null; }
        if (meridiem === 'p' && hour !== 12) { hour += 12; }
        if (meridiem === 'a' && hour === 12) { hour = 0; }
    } else if (hour > 23) {
        return null;
    }
    return { hour, minute };
}

/** Render a cutoff back to the canonical "h:mm A" text (what the settings UI edits). */
export function formatCutoffTime(cutoff: CutoffTime | null): string | null {
    if (!cutoff) { return null; }
    const h12 = cutoff.hour % 12 === 0 ? 12 : cutoff.hour % 12;
    const mm = cutoff.minute < 10 ? '0' + cutoff.minute : '' + cutoff.minute;
    return `${h12}:${mm} ${cutoff.hour < 12 ? 'AM' : 'PM'}`;
}

/**
 * Parse an ONLINE_DAY_END value into a policy. Never throws; anything
 * unrecognized degrades to the legacy fallback (tomorrow, no cutoff).
 */
export function parseBookingDayPolicy(raw: string | null | undefined): BookingDayPolicy {
    const text = (raw || '').trim();
    if (!text) { return { ...DEFAULT_BOOKING_DAY_POLICY }; }

    if (/^past$/i.test(text)) { return { minOffset: null, cutoff: null }; }

    const offsetForm = /^D(-?\d+)(?:\|(.*))?$/i.exec(text);
    if (offsetForm) {
        const cutoff = offsetForm[2] !== undefined ? parseCutoffTime(offsetForm[2]) : null;
        return { minOffset: +offsetForm[1], cutoff };
    }

    // Legacy: a bare time means "tomorrow until this time, then day after tomorrow".
    const legacyCutoff = parseCutoffTime(text);
    if (legacyCutoff) { return { minOffset: 1, cutoff: legacyCutoff }; }

    return { ...DEFAULT_BOOKING_DAY_POLICY };
}

/** Serialize a policy to its canonical stored string. */
export function formatBookingDayPolicy(policy: BookingDayPolicy): string {
    if (policy.minOffset === null) { return 'PAST'; }
    const base = `D${policy.minOffset}`;
    const cutoffText = formatCutoffTime(policy.cutoff);
    return cutoffText ? `${base}|${cutoffText}` : base;
}

/**
 * The app-wide "current" INSTANT: the server-synced `window.currentdate`
 * getter when present (correct even offline — epoch time is timezone-
 * independent), else the machine clock. All wall-clock/day decisions on it go
 * through the IST helpers below — the temple runs on Kerala time and the
 * booking window must flip at TEMPLE time no matter where the browser is.
 */
export function bookingNow(): Date {
    const current = typeof window !== 'undefined' ? (window as any).currentdate : null;
    return current ? new Date(current) : new Date();
}

/**
 * The earliest bookable date under a policy — an instant at IST MIDNIGHT of
 * the minimum IST calendar day — or null when the past is unlimited. After
 * the cutoff time (IST wall clock) the minimum moves one day later; exactly
 * at the cutoff minute still counts as "before" (legacy isSameOrBefore).
 */
export function effectiveMinBookingDate(policy: BookingDayPolicy, now: Date = bookingNow()): Date | null {
    if (policy.minOffset === null) { return null; }
    let offset = policy.minOffset;
    const nowIst = istParts(now);
    if (policy.cutoff) {
        const nowMinutes = nowIst.hour * 60 + nowIst.minute;
        const cutoffMinutes = policy.cutoff.hour * 60 + policy.cutoff.minute;
        if (nowMinutes > cutoffMinutes) { offset += 1; }
    }
    return new Date(istEpoch(nowIst.year, nowIst.month, nowIst.day + offset));
}

/** Read the temple stored by login/portal startup (localStorage 'temple' is a JSON array). */
export function templeOnlineDayEnd(): string | null {
    try {
        const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('temple') : null;
        if (!stored) { return null; }
        const temples = JSON.parse(stored);
        const temple = Array.isArray(temples) ? temples[0] : temples;
        return temple ? (temple.onlineDayEnd || null) : null;
    } catch {
        return null;
    }
}

/**
 * The ONLINE minimum bookable date derived from the stored temple, or null when
 * the policy is PAST (no lower bound).
 */
export function minBookingDateFromTemple(now: Date = bookingNow()): Date | null {
    return effectiveMinBookingDate(parseBookingDayPolicy(templeOnlineDayEnd()), now);
}

/**
 * The COUNTER minimum: `min(effectiveMin, today)` — a past window (D-N/PAST)
 * extends counter back-dating, but a forward offset never blocks same-day
 * counter billing (owner decision 2026-07-16). null = unlimited past.
 */
export function counterMinBookingDate(now: Date = bookingNow()): Date | null {
    const online = minBookingDateFromTemple(now);
    if (online === null) { return null; }
    const today = new Date(istStartOfDay(now));
    return online < today ? online : today;
}

/** True inside the Tauri desktop (counter) app — same check app-routing uses. */
export function isCounterApp(): boolean {
    return typeof window !== 'undefined' && typeof (window as any).__TAURI__ !== 'undefined';
}

/**
 * Context-sensitive minimum for components shared by BOTH apps (panchang
 * calendar, continuous booking): counter rule on the desktop app, online rule
 * on the devotee portal. null = unlimited past.
 */
export function contextMinBookingDate(now: Date = bookingNow()): Date | null {
    return isCounterApp() ? counterMinBookingDate(now) : minBookingDateFromTemple(now);
}
